import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import type { ClosingStepView } from "@/lib/closingConversation";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Textarea } from "@/ui/Input";
import { Tooltip } from "@/ui/Tooltip";

/**
 * A closing conversation's ordered steps, with stable read-only ids (#867).
 *
 * ## THE REFERENCE'S SHAPE
 *
 * The [Pathway detail](https://noproductcontent.lovable.app/library/pathways/ptsd)
 * screen renders one compact row per named step, and the [BG domain
 * editor](https://noproductcontent.lovable.app/library/bg-sets/domain/family_childhood)
 * renders one card per step with the text edited IN PLACE and `Add prompt`
 * under the list. Closing is an EXPLICIT ADDITION to Lovable — it has no
 * reference screen of its own — so it reuses the second: a closing step IS its
 * instruction, there is no second screen to open, and the S7 screen contract
 * says to reuse the "named prompt/step editors" rather than invent a layout.
 *
 * Two things the reference rows do not carry, both from the contract rather
 * than from taste:
 *
 *   * **The step id is on the row, read-only.** It is the stable key the pinned
 *     engine addresses a step by and the key clinical memory's skip/enrich maps
 *     resolve — never the display position, which `format_steps` renumbers from
 *     1 over the steps it did not skip. A clinician reordering steps has to be
 *     able to tell which record each row IS.
 *   * **The position is stated beside it.** The two differ the moment a step is
 *     moved, and a surface showing only one of them would let a clinician read
 *     "3." as the identity the runtime uses.
 *
 * ## REORDER IS BUTTONS, WITH `BackgroundItemRows`' FOCUS AND ANNOUNCEMENT RULES
 *
 * Two buttons are operable by keyboard, pointer and assistive technology on the
 * first day, which is #455's requirement and the reason every reorder control in
 * this application is a pair. The behaviour AFTER the press is
 * `PathwayStepList`'s and `BackgroundItemRows`', restated here for the same two
 * defects both document: focus moves to the pair's remaining operable half when
 * the pressed control becomes inert, and a polite live region says where the row
 * landed.
 *
 * Not merged with either of them, and this is the same reasoning both give for
 * not merging with each other: `PathwayStepList` renders rows whose subject is a
 * stored PART and offers Open, `BackgroundItemRows` renders items keyed by an
 * opaque string id inside a domain document, and these rows edit an integer-keyed
 * step inside a document with a completion rule neither family has. Rewriting a
 * component two other surfaces depend on, from a ticket scoped to Closing, is how
 * a scoped change becomes an unreviewable one. Reconciling the three is a
 * follow-up named in this pull request rather than left to be discovered.
 */
export function ClosingStepRows({
  steps,
  language,
  readOnly,
  readOnlyReason,
  listLabel,
  onChangeInstruction,
  onMove,
  onRemove,
  add,
}: {
  steps: readonly ClosingStepView[];
  /** The stored document's language, for the textarea's `lang`, or `null`. */
  language: string | null;
  readOnly: boolean;
  readOnlyReason: string | null;
  /** The list's accessible name — carries the language when there are several. */
  listLabel: string;
  onChangeInstruction: (index: number, value: string) => void;
  /** Move the step at `index` to the absolute position `to`. */
  onMove: (index: number, to: number) => void;
  onRemove: (index: number) => void;
  add?: {
    label: string;
    /** Why adding is refused, or `null`. Never an unexplained disabled button. */
    refusal: string | null;
    onAdd: () => void;
  };
}) {
  // WHICH control should hold focus once the moved order has rendered, keyed by
  // the step's stable ID rather than by its index — the index is exactly what
  // changed. A ref rather than state: nothing renders from it, so state would
  // mean a second render per move whose only job is to forget it.
  const restore = useRef<{ id: string; direction: "up" | "down" } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const controls = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    const pending = restore.current;
    if (pending === null) {
      return;
    }
    restore.current = null;
    const index = steps.findIndex((step) => String(step.id) === pending.id);
    if (index === -1) {
      // The step left the list between the move and this effect — somebody
      // else's version landed and re-seeded the draft. Guessing a neighbour
      // would move focus somewhere the clinician did not ask for.
      return;
    }
    const stillOperable =
      pending.direction === "up" ? index > 0 : index < steps.length - 1;
    if (!stillOperable) {
      const opposite = pending.direction === "up" ? "down" : "up";
      controls.current.get(`${pending.id}:${opposite}`)?.focus();
    }
  }, [steps]);

  function move(step: ClosingStepView, index: number, direction: "up" | "down") {
    const to = direction === "up" ? index - 1 : index + 1;
    restore.current = { id: String(step.id), direction };
    setAnnouncement(
      `Step ${String(step.id)} moved to position ${to + 1} of ${steps.length}.`,
    );
    onMove(index, to);
  }

  return (
    <div className="space-y-3">
      {/* Mounted unconditionally and empty until a move happens: a live region
          added to the DOM at the same moment it gains text is frequently not
          announced at all. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {steps.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          This closing conversation has no steps yet. The pinned engine drives
          the closing turn from this list, so a version it can run needs at least
          one.
        </p>
      ) : (
        <ol aria-label={listLabel} className="space-y-3">
          {steps.map((step, index) => (
            <ClosingStepRow
              key={`${String(step.id)}:${index}`}
              step={step}
              index={index}
              total={steps.length}
              language={language}
              readOnly={readOnly}
              readOnlyReason={readOnlyReason}
              onChangeInstruction={(value) => onChangeInstruction(index, value)}
              onMove={(direction) => move(step, index, direction)}
              onRemove={() => onRemove(index)}
              registerControl={(direction, element) => {
                const key = `${String(step.id)}:${direction}`;
                if (element) controls.current.set(key, element);
                else controls.current.delete(key);
              }}
            />
          ))}
        </ol>
      )}

      {readOnly || add === undefined ? null : (
        // UNDER the rows, where the BG domain editor puts `Add prompt`.
        <Tooltip content={add.refusal ?? add.label}>
          <Button
            variant="outline"
            size="sm"
            disabled={add.refusal !== null}
            onClick={add.onAdd}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {add.label}
          </Button>
        </Tooltip>
      )}
    </div>
  );
}

function ClosingStepRow({
  step,
  index,
  total,
  language,
  readOnly,
  readOnlyReason,
  onChangeInstruction,
  onMove,
  onRemove,
  registerControl,
}: {
  step: ClosingStepView;
  index: number;
  total: number;
  language: string | null;
  readOnly: boolean;
  readOnlyReason: string | null;
  onChangeInstruction: (value: string) => void;
  onMove: (direction: "up" | "down") => void;
  onRemove: () => void;
  registerControl: (
    direction: "up" | "down",
    element: HTMLButtonElement | null,
  ) => void;
}) {
  const instructionId = useId();
  const id = String(step.id);

  return (
    <li className="rounded-md border border-border bg-card px-3 py-2.5">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <span className="w-6 shrink-0 pt-1.5 text-xs tabular-nums text-muted-foreground">
          {index + 1}.
        </span>

        <div className="min-w-0 grow basis-64 space-y-1.5">
          {/* THE LABEL IS THE STABLE ID, and it is a real `<label>` for the
              textarea rather than decoration: a page of steps named only
              "Instruction" is the repeated-ambiguous-control defect the parity
              audit already recorded once. `dir="ltr"` because an id is a machine
              identifier sitting beside Hebrew prose, and the surrounding
              bidirectional context would otherwise reorder it. */}
          <label
            htmlFor={instructionId}
            className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
          >
            <span>
              Step id{" "}
              <code
                dir="ltr"
                className="rounded bg-muted px-1 py-0.5 font-mono text-foreground"
              >
                {id}
              </code>
            </span>
            {/* THE POSITION, said separately from the id. They diverge on the
                first move, and the runtime addresses only the id. */}
            <span>{`position ${index + 1} of ${total}`}</span>
          </label>

          <Textarea
            id={instructionId}
            rows={3}
            value={step.instruction}
            readOnly={readOnly}
            // The stored clinical text is Hebrew on both MOD and NII, and the
            // browser's own bidirectional algorithm is what gets this right for
            // a mixed string — `auto` reads the first strong character rather
            // than this component asserting a direction the content may not
            // have.
            dir="auto"
            {...(language ? { lang: language } : {})}
            onChange={(event) => onChangeInstruction(event.currentTarget.value)}
          />
        </div>

        {readOnly ? null : (
          <div className="flex items-center gap-0.5 pt-1">
            {/* THE STEP'S OWN ID IS IN EVERY LABEL, and the POSITION is
                deliberately not: it changes on every move, and a name that
                changes under a focused control is re-announced as a different
                button. */}
            <IconButton
              ref={(element) => registerControl("up", element)}
              label={`Move step ${id} up`}
              icon={<ArrowUp className="h-4 w-4" />}
              disabled={index === 0}
              onClick={() => onMove("up")}
            />
            <IconButton
              ref={(element) => registerControl("down", element)}
              label={`Move step ${id} down`}
              icon={<ArrowDown className="h-4 w-4" />}
              disabled={index === total - 1}
              onClick={() => onMove("down")}
            />
            <Tooltip content={readOnlyReason ?? `Remove step ${id}`}>
              <IconButton
                label={`Remove step ${id}`}
                icon={<Trash2 className="h-4 w-4" />}
                onClick={onRemove}
              />
            </Tooltip>
          </div>
        )}
      </div>
    </li>
  );
}
